// src/components/AsistenciaList.tsx
import React, { useEffect, useState, useRef } from "react";
//import axios from "axios";
import axiosInstance from "../utils/axiosInstance";
import Card from "components/card";
import Dropdown from "components/dropdown";
import { BsThreeDots } from "react-icons/bs";
import { CiCalendar } from "react-icons/ci";
import { BiShareAlt } from "react-icons/bi";
import { format } from "date-fns";
import WideModal from "components/WideModal";
import Modal from "components/Modal";
import {
  Button,
  EmptyState,
  Pagination,
  SectionHeader,
} from "components/ui";
import {
  Zona,
  compararPorZona,
  compiteEnLaTabla,
  porcentajeDeDescenso,
  zonaPorPorcentaje,
} from "utils/zonas";
import { GiSoccerBall, GiBarbecue } from "react-icons/gi";
import { MdKeyboardArrowDown } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import { useYear } from "context/YearContext"; 
import AttendanceCard from "components/share/attendance-card";


import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";

//import html2canvas from "html2canvas";
import { toBlob, toSvg } from "html-to-image";
import { Tooltip } from "@mui/material";

/* ===================== TYPES FROM YOUR SERVER ===================== */
interface ResumenAsistencia {
  id: number;
  nombre: string;
  createdAt: string;
  asistencias: number;
  partialTotal: number; // from server
  porcentaje: string;   // e.g. "85"
  color: string;        // e.g. "bg-blue-100"
  deletedAt?: string | null;
}

interface Evento {
  id: number;
  fecha: string;
  tipo: string;
  cancelado: boolean;
  Asistencia: {
    PersonaId: number;
    presente: boolean;
  }[];
  _dayOnly?: number;
}


const today = new Date();
const formattedDate = today.toLocaleDateString('en-GB'); 

/* ===================== ZONAS DE LA TABLA ===================== */
/**
 * Los umbrales son los mismos que ya aplica el backend en eventos.js
 * (75% y 25%) para elegir el color de cada jugador. Acá se reutilizan para
 * agrupar la lista, así la separación visual y el color del servidor nunca
 * pueden contradecirse.
 */
const zonaDe = (item: ResumenAsistencia): Zona =>
  zonaPorPorcentaje(Number(item.porcentaje) || 0, item.partialTotal);

const porcentajeDe = (item: ResumenAsistencia) => Number(item.porcentaje) || 0;

const datosDescensoDe = (item: ResumenAsistencia) => ({
  porcentaje: porcentajeDe(item),
  totalEventos: item.partialTotal,
  dadoDeBaja: Boolean(item.deletedAt) || item.color?.includes("gray"),
});

/** Franja de color al costado de la fila: dice de un vistazo en qué zona está. */
const bordeDeZona: Record<Zona, string> = {
  alta: "border-l-green-600",
  media: "border-l-amber-400",
  descenso: "border-l-red-400",
  "sin-datos": "border-l-gray-300 dark:border-l-white/20",
};

/** Aire entre tramos de color, sin rótulo: el corte se lee solo. */
const EspacioEntreTramos = () => <li aria-hidden="true" className="h-4" />;

/**
 * Corte del descenso.
 *
 * Marca el último puesto de la tabla, no la franja de porcentaje bajo: que
 * varios estén en rojo no los pone a todos en descenso.
 */
const SeparadorDescenso = () => (
  <li className="flex select-none items-center gap-2.5 pb-2.5 pt-4">
    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-red-600 dark:text-red-400">
      Descenso
    </span>
    <span className="h-px flex-1 bg-red-200 dark:bg-red-500/25" />
  </li>
);

/* ===================== HELPER FOR DATES ===================== */
const formatDate = (isoDate: string) => format(new Date(isoDate), "dd/MM/yy");

/* ===================== STAGGER ANIMATION VARIANTS ===================== */
const attendeeContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.025 } },
};
const attendeeItem = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.18 } },
};

/* ===================== YEAR MODAL ===================== */
function YearModal({
  isOpen,
  onClose,
  availableYears,
  selectedYear,
  onSelectYear,
}: {
  isOpen: boolean;
  onClose: () => void;
  availableYears: number[];
  selectedYear: number;
  onSelectYear: (year: number) => void;
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="select-none">
        <h3 className="text-[17px] font-semibold text-gray-900 dark:text-white">
          Historial
        </h3>
        <p className="mt-1 text-sm text-gray-700 dark:text-gray-400">
          Elegí la temporada que querés ver.
        </p>

        {availableYears.length === 0 ? (
          <p className="mt-5 text-sm text-gray-700 dark:text-gray-400">
            No hay años disponibles.
          </p>
        ) : (
          /* Grilla de años: se leen de un vistazo y el activo queda marcado */
          <div className="mt-5 grid grid-cols-3 gap-2">
            {availableYears.map((year) => {
              const isCurrent = year === selectedYear;
              return (
                <button
                  key={year}
                  type="button"
                  disabled={isCurrent}
                  aria-current={isCurrent ? "true" : undefined}
                  onClick={() => onSelectYear(year)}
                  className={`tabular h-10 rounded-control border text-sm font-semibold transition-colors duration-150 ${
                    isCurrent
                      ? "cursor-default border-brand-500 bg-brand-500 text-white"
                      : "border-gray-200 bg-white text-gray-900 hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:hover:bg-white/5"
                  }`}
                >
                  {year}
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ===================== HISTORICO BUTTON ===================== */
function HistoricoButton({
  onOpenYearModal,
}: {
  onOpenYearModal: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Dropdown
      button={
        <Button
          variant="ghost"
          size="icon"
          aria-label="Más opciones"
          onClick={() => setOpen(!open)}
        >
          <BsThreeDots className="h-[18px] w-[18px]" />
        </Button>
      }
      classNames="top-11 right-0 w-max"
    >
      <div className="w-max select-none overflow-hidden rounded-card border border-gray-200/80 bg-white p-1.5 shadow-pop dark:border-white/10 dark:!bg-navy-700">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center gap-2.5 rounded-control px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
          onClick={() => {
            setOpen(false);
            onOpenYearModal(); // open the modal
          }}
        >
          <CiCalendar className="h-[18px] w-[18px] shrink-0 text-gray-600 dark:text-gray-500" />
          Historial
        </button>
      </div>
    </Dropdown>
  );
}

/* ===================== Skeleton loader ===================== */
const SkeletonRow = () => (
  <li className="mb-3 rounded-card border border-gray-200/70 bg-white p-4 dark:border-white/5 dark:bg-navy-900">
    <div className="flex items-center justify-between gap-4">
      <div className="skeleton h-4 w-28 rounded-full" />
      <div className="flex items-center gap-3">
        <div className="skeleton h-4 w-10 rounded-full" />
        <div className="skeleton h-4 w-12 rounded-full" />
      </div>
    </div>
    <div className="skeleton mt-3.5 h-2 w-full rounded-full" />
  </li>
);

/* ===================== PROPS: add refreshKey ===================== */
const AsistenciaList = ({ refreshKey }: { refreshKey?: number }) => {
  const [resumen, setResumen] = useState<ResumenAsistencia[]>([]);
  const [eventsThisYear, setEventsThisYear] = useState<Evento[]>([]);
  const [expandedPlayer, setExpandedPlayer] = useState<number | null>(null);

  // Modal: choose year
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [yearModalOpen, setYearModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const { setYear } = useYear();

  // Loading + Error
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Table pagination & sorting
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 9 });
  const [sorting, setSorting] = useState<SortingState>([]);

  // SHARE MODAL
  const [showShareModal, setShowShareModal] = useState(false);

  // FADE EFFECT
  const [fadeIn, setFadeIn] = useState(false);


  const fullListRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const [isDesktop, setIsDesktop] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768); // adjust threshold as needed
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Capture + share the entire list
// replace the entire handleShareToWhatsApp function with this
const handleShareToWhatsApp = async () => {
  if (!fullListRef.current) return;
  setIsExporting(true);

  const originalNode = fullListRef.current as HTMLElement;

  // 1) wait for fonts
  try {
    if ((document as any)?.fonts?.ready) await (document as any).fonts.ready;
  } catch (e) {
    console.warn("fonts.ready failed:", e);
  }

  // 2) clone node
  const clone = originalNode.cloneNode(true) as HTMLElement;
  clone.removeAttribute("id");

  // 3) wrapper offscreen but horizontally centered (avoid big left gap)
  const wrapper = document.createElement("div");
  wrapper.style.position = "fixed";
  wrapper.style.top = "-20000px";           // move way above viewport (safe)
  wrapper.style.left = "50%";               // center horizontally
  wrapper.style.transform = "translateX(-50%)";
  wrapper.style.zIndex = "2147483647";
  wrapper.style.pointerEvents = "none";
  wrapper.style.background = "#ffffff";
  wrapper.style.opacity = "1";
  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  // 4) apply safe export styles (remove clipping, transitions, animations)
  const applySafeStyles = (el: HTMLElement) => {
    el.style.maxHeight = "none";
    el.style.height = "auto";
    el.style.overflow = "visible";
    el.style.transform = "none";
    el.style.transition = "none";
    el.style.animation = "none";
    // don't touch inline backgrounds / widths - we want them
  };
  applySafeStyles(clone);
  clone.querySelectorAll<HTMLElement>("*").forEach(applySafeStyles);

  // 5) measure sizes from original
  const scrollW = Math.max(originalNode.scrollWidth, originalNode.getBoundingClientRect().width);
  const scrollH = Math.max(originalNode.scrollHeight, originalNode.getBoundingClientRect().height);

  // 6) compute export width (clamped) and set it on clone
  const minExportWidth = isDesktop ? 900 : 720;
  const maxExportWidth = isDesktop ? 1100 : 900;
  const exportWidth = Math.min(Math.max(scrollW, minExportWidth), maxExportWidth);

  clone.style.width = `${exportWidth}px`;
  clone.style.minWidth = `${exportWidth}px`;

  // recompute height after forcing width
  await new Promise((r) => requestAnimationFrame(r));
  const exportHeight = Math.max(clone.scrollHeight, scrollH);
  clone.style.height = `${exportHeight}px`;
  clone.style.minHeight = `${exportHeight}px`;

  // 7) wait images in the clone to load
  const waitForImages = (root: HTMLElement) => {
    const imgs = Array.from(root.querySelectorAll<HTMLImageElement>("img"));
    return Promise.all(
      imgs.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) return resolve();
            const onEnd = () => {
              img.removeEventListener("load", onEnd);
              img.removeEventListener("error", onEnd);
              resolve();
            };
            img.addEventListener("load", onEnd);
            img.addEventListener("error", onEnd);
            setTimeout(resolve, 5000);
          })
      )
    );
  };

  try {
    await waitForImages(clone);

    // allow two frames + a tiny timeout so layout and paint settle (important)
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => setTimeout(r, 80));
    clone.getBoundingClientRect();

    // --- CRITICAL: convert percentage-based bars into pixel widths inside the clone ---
    // Priority: elements with data-progress (numeric 0..100)
    const dataBars = Array.from(clone.querySelectorAll<HTMLElement>("[data-progress]"));
    if (dataBars.length > 0) {
      dataBars.forEach((bar) => {
        const pct = Math.max(0, Math.min(100, Number(bar.getAttribute("data-progress")) || 0));
        const parent = bar.parentElement as HTMLElement | null;
        if (!parent) return;
        const parentW = Math.max(1, Math.round(parent.getBoundingClientRect().width));
        const px = pct === 0 ? 0 : Math.max(Math.round(parentW * (pct / 100)), Math.min(6, Math.round(parentW * 0.02)));
        // El alto lo declara la propia barra: estaba fijo en 12px, así que
        // cualquier cambio de diseño en la tarjeta salía distinto en la imagen.
        const barH = `${Number(bar.getAttribute("data-bar-height")) || 12}px`;
        bar.style.width = `${px}px`;
        bar.style.height = barH;   // restore height collapsed by applySafeStyles
        bar.style.transition = "none";
        bar.style.transform = "none";
        // restore container height (applySafeStyles sets height:auto which collapses empty divs)
        parent.style.height = barH;
        parent.style.minHeight = barH;
      });
    } else {
      // fallback: try to find `.progress-inner` and look for a nearby percentage text like "81%" to infer percent
      const fallbackBars = Array.from(clone.querySelectorAll<HTMLElement>(".progress-inner, .progress-bar, [data-progress-perc]"));
      if (fallbackBars.length > 0) {
        fallbackBars.forEach((bar) => {
          let pct = 0;
          // 1) check attribute data-progress-perc
          const attr = bar.getAttribute("data-progress-perc");
          if (attr) pct = Number(attr);
          // 2) check sibling text that looks like "81%"
          if (!attr) {
            const maybeText = Array.from(bar.parentElement?.parentElement?.querySelectorAll("div, span") || [])
              .map((n) => n.textContent || "")
              .find((t) => /\b\d{1,3}%\b/.test(t || ""));
            if (maybeText) {
              const m = maybeText!.match(/(\d{1,3})%/);
              if (m) pct = Number(m[1]);
            }
          }
          const parent = bar.parentElement as HTMLElement | null;
          if (!parent) return;
          const parentW = Math.max(1, Math.round(parent.getBoundingClientRect().width));
          const px = Math.max(Math.round(parentW * (Math.max(0, Math.min(100, pct)) / 100)), Math.min(6, Math.round(parentW * 0.02)));
          bar.style.width = `${px}px`;
          bar.style.transition = "none";
          bar.style.transform = "none";
        });
      }
    }

    // 8) export with different pixel ratios
    let blob: Blob | null = null;
    const deviceDPR = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    const pixelRatios = [Math.min(3, Math.round(deviceDPR * 2)), Math.min(2, Math.round(deviceDPR)), 1];

    for (const ratio of pixelRatios) {
      try {
        blob = await toBlob(clone as HTMLElement, {
          width: exportWidth,
          height: exportHeight,
          pixelRatio: ratio,
          backgroundColor: "#ffffff",
          cacheBust: true,
        });
        if (blob) {
          console.log("export success ratio:", ratio, "exportWidth:", exportWidth, "exportHeight:", exportHeight);
          break;
        }
      } catch (err) {
        console.warn("toBlob failed ratio", ratio, err);
        blob = null;
      }
    }

    // SVG fallback
    if (!blob) {
      try {
        const svgString = await toSvg(clone as HTMLElement, { cacheBust: true });
        blob = await svgToPngBlob(svgString, exportWidth, exportHeight, 2);
      } catch (err) {
        console.error("SVG fallback failed:", err);
        blob = null;
      }
    }

    if (!blob) throw new Error("No se generó blob");

    // prepare file
    const file = new File([blob], `asistencias_${selectedYear}.png`, { type: "image/png" });

    // restore UI ASAP
    setIsExporting(false);
    if (document.body.contains(wrapper)) document.body.removeChild(wrapper);

    // share / download
    if (isDesktop) {
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      window.open(`https://wa.me/?text=${encodeURIComponent(`Asistencias ${selectedYear} · Imagen descargada.`)}`, "_blank");
    } else {
      if ((navigator as any).canShare && (navigator as any).canShare({ files: [file] })) {
        await (navigator as any).share({ files: [file], text: `Asistencias ${selectedYear}` });
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(`Asistencias ${selectedYear} · Imagen generada.`)}`, "_blank");
      }
    }
    return;
  } catch (err) {
    console.error("Export error:", err);
    try {
      if (document.body.contains(wrapper)) document.body.removeChild(wrapper);
    } catch {}
    setIsExporting(false);
    alert("No se pudo generar la imagen completa. Revisa la consola para más detalles.");
  }
};
/** Helper: convierte string SVG -> PNG Blob a una escala dada (scale) */
async function svgToPngBlob(svgString: string, width: number, height: number, scale = 2): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("No se pudo obtener contexto canvas");
        // fondo blanco (opcional)
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((b) => {
          URL.revokeObjectURL(url);
          if (!b) reject(new Error("toBlob failed"));
          else resolve(b);
        }, "image/png");
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    // Important: set crossOrigin if necessary, but objectURL avoids CORS in most cases
    img.src = url;
  });
}

  /* =========== 1) FETCH A SPECIFIC YEAR'S RESUMEN =========== */
  const fetchDataForYear = async (year: number) => {
    try {
      setFadeIn(false);
      setLoading(true);
      setError(null);
      
      const resumenRes = await axiosInstance.get(
        `/eventos/resumen?year=${year}&includeDeleted=true`
      );
      const serverData = resumenRes.data as ResumenAsistencia[];

      // Por zona y después por porcentaje: si se ordena sólo por porcentaje,
      // un jugador dado de baja (que el servidor marca en gris) cae en el medio
      // del descenso y parte el bloque en dos.
      const sortedData = serverData.sort(compararPorZona(zonaDe, porcentajeDe));
      setResumen(sortedData);

      // Next, fetch all events for that year
      const allEvents = await axiosInstance.get("/eventos");
      const eventData = allEvents.data as Evento[];
      // Filter for that year + not canceled
      const yearEvents = eventData.filter(
        (evt) =>
          new Date(evt.fecha).getFullYear() === year && evt.cancelado === false
      )

      // SORT BY DATE (newest first)
      .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())

      // add _dayOnly
      yearEvents.forEach((evt) => {
        const d = new Date(evt.fecha);
        d.setHours(0, 0, 0, 0);
        evt._dayOnly = d.getTime();
      });
      setEventsThisYear(yearEvents);

      setSelectedYear(year);
      // sincroniza con el contexto global para que el Dashboard reaccione
      setYear(year);
    } catch (err) {
      console.error("Error fetchDataForYear:", err);
      setError("Error al obtener los datos para el año " + year);
    } finally {
      setLoading(false);
      setHasLoaded(true);
      setFadeIn(true);
    }
  };

  /* =========== 2) FETCH ALL EVENTS TO PARSE DISTINCT YEARS =========== */
  const fetchAllEvents = async () => {
    try {
      const { data: rawEvents } = await axiosInstance.get("/eventos");
      const events = rawEvents as Evento[];
      // parse distinct years
      const yearsSet = new Set<number>();
      events.forEach((evt) => {
        const y = new Date(evt.fecha).getFullYear();
        yearsSet.add(y);
      });

      // Always include the current year so the modal shows it even when there are no events yet
      const currentYear = new Date().getFullYear();
      yearsSet.add(currentYear);

      // convert to array + sort descending
      const yearsArray = Array.from(yearsSet).sort((a, b) => b - a);
      setAvailableYears(yearsArray);
    } catch (err) {
      console.error("Error fetching all events for distinct years:", err);
    }
  };


  /* =========== Run fetches ON MOUNT + whenever refreshKey changes ========== */
  useEffect(() => {
    fetchDataForYear(selectedYear);
    fetchAllEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]); 
  // ^ "selectedYear" is also relevant, but we have a separate year selection that calls fetchDataForYear.
  //   If you want to also refetch on user changing the year, you can put [refreshKey, selectedYear]

  // If you want the year to refetch automatically whenever user changes it + refreshKey changes,
  // you can do: useEffect(() => { fetchDataForYear(selectedYear); fetchAllEvents(); }, [selectedYear, refreshKey]);

  /* =========== TANSTACK TABLE SETUP =========== */
  const columnHelper = createColumnHelper<ResumenAsistencia>();
  const columns: ColumnDef<ResumenAsistencia, any>[] = [
    columnHelper.accessor("nombre", {
      header: "Jugador",
      cell: (info) => info.getValue(),
    }),
    {
      id: "asistenciasSlash",
      header: "Presente / Total",
      cell: (info) => {
        const row = info.row.original;
        return `${row.asistencias}/${row.partialTotal}`;
      },
    },
    columnHelper.accessor("porcentaje", {
      id: "porcentaje",
      header: "Porcentaje",
      cell: (info) => `${info.getValue()}%`,
    }),
  ];

  const table = useReactTable({
    data: resumen,
    columns,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  /**
   * Quién está en el último puesto.
   *
   * Se calcula sobre el resumen completo y no sobre la página visible: el
   * fondo de la tabla no cambia porque estés mirando la página 1.
   */
  const porcentajeDescenso = porcentajeDeDescenso(resumen, datosDescensoDe);
  const estaEnDescenso = (item: ResumenAsistencia) =>
    porcentajeDescenso !== null &&
    compiteEnLaTabla(datosDescensoDe(item)) &&
    porcentajeDe(item) === porcentajeDescenso;

  const toggleExpand = (playerId: number) => {
    setExpandedPlayer((prev) => (prev === playerId ? null : playerId));
  };

  /* =========== EVENTOS DEL AÑO SEPARADOS POR TIPO (para la imagen compartida) =========== */
  const partidosCount = eventsThisYear.filter((evt) => evt.tipo === "Fútbol").length;
  const penasCount = eventsThisYear.filter((evt) => evt.tipo === "Peña").length;

  return (
    <div className="relative select-none">
      {/* YEAR MODAL */}
      <YearModal
        isOpen={yearModalOpen}
        onClose={() => setYearModalOpen(false)}
        availableYears={availableYears}
        selectedYear={selectedYear}
        onSelectYear={(year) => {
          setYearModalOpen(false);
          fetchDataForYear(year);
        }}
      />

      {/* SHARE MODAL with "no pagination" table */}
      <WideModal isOpen={showShareModal} onClose={() => setShowShareModal(false)}>
        <div className="mx-auto w-full max-w-2xl select-none">
          <h3 className="text-[17px] font-semibold text-gray-900 dark:text-white">
            {`Lista completa ${selectedYear}`}
          </h3>
          <p className="mb-4 mt-1 text-sm text-gray-700 dark:text-gray-400">
            Vista previa de la imagen que se va a compartir.
          </p>

          {/* This container has a scroll, which we'll override in handleShareToWhatsApp */}
          <div
            className="max-h-[60vh] overflow-y-auto overflow-x-auto"
            ref={fullListRef}
          >
            <AttendanceCard
              resumen={resumen}
              title="Asistencias"
              season={`Temporada ${selectedYear}`}
              updatedAt={formattedDate}
              totalPartidos={partidosCount}
              totalPenas={penasCount}
              isExporting={isExporting}
            />
          </div>

          {/* Sticky footer */}
          <div className="sticky bottom-0 left-0 right-0 mt-4 select-none border-t border-gray-200/80 bg-white pb-1 pt-4 dark:border-white/10 dark:bg-navy-800">
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowShareModal(false)}>
                Cerrar
              </Button>
              <Button
                variant="primary"
                onClick={handleShareToWhatsApp}
                loading={isExporting}
                leftIcon={!isExporting ? <BiShareAlt className="h-4 w-4" /> : undefined}
              >
                {isDesktop ? "Descargar imagen" : "Compartir imagen"}
              </Button>
            </div>
          </div>
        </div>
      </WideModal>

      <div
        className={
          yearModalOpen
            ? "opacity-25 pointer-events-none transition duration-200"
            : "transition duration-200"
        }
      >
        <Card extra="flex h-full min-h-[560px] w-full flex-col p-5 text-left sm:p-6">
          {/* HEADER */}
          <SectionHeader
            title="Asistencias"
            description={`Presencia por jugador · Temporada ${selectedYear}`}
            actions={
              <>
              <HistoricoButton onOpenYearModal={() => setYearModalOpen(true)} />
              {/* ---- 🔽 ADD THIS HIDDEN SELECT (1 block) ----
                  Dashboard script will find select[data-historial-year] and listen to changes.
                  We keep it hidden so UI doesn't change, but keep it controlled by React.
              */}
              <select
                data-historial-year="1"
                value={selectedYear}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  // keep existing behavior: close modal and fetch for selected year
                  setYearModalOpen(false);
                  fetchDataForYear(y);
                }}
                className="sr-only" // utility class that hides visually but keeps in DOM for accessibility
                aria-hidden="true"
              >
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              {/* Año actual (solo si no estamos en él) */}
              {selectedYear !== new Date().getFullYear() && (
              <Tooltip
                title="Volver al año actual"
                arrow
                componentsProps={{
                  tooltip: {
                    className:
                      "!bg-white dark:!bg-navy-700 !text-navy-700 dark:!text-white " +
                      "!text-[13px] !font-medium !shadow-pop !rounded-control !px-3 !py-2 " +
                      "!border !border-gray-200/80 dark:!border-white/10",
                  },
                  arrow: {
                    className: "!text-white dark:!text-navy-700",
                  },
                }}
              >
                <Button
                  variant="soft"
                  size="sm"
                  className="tabular"
                  onClick={() => fetchDataForYear(new Date().getFullYear())}
                  aria-label={`Seleccionar año ${new Date().getFullYear()}`}
                >
                  {new Date().getFullYear()}
                </Button>
              </Tooltip>

              )}

              <Button
                variant="ghost"
                size="icon"
                aria-label="Compartir lista de asistencias"
                onClick={() => setShowShareModal(true)}
              >
                <BiShareAlt className="h-[18px] w-[18px]" />
              </Button>
              </>
            }
          />

          {/* TABLE (with pagination + expansions) */}
          {/* La sombra pesada del contenedor se fue: cada fila ya tiene su borde */}
          <div className="flex-1">
            {error && (
              <EmptyState
                tone="danger"
                title="No se pudieron cargar las asistencias"
                description={error}
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => fetchDataForYear(selectedYear)}
                  >
                    Reintentar
                  </Button>
                }
              />
            )}
            {hasLoaded && !loading && !error && resumen.length === 0 && (
              <EmptyState
                icon={<GiSoccerBall />}
                title={`Sin asistencias en ${selectedYear}`}
                description="Cuando se registren eventos de esta temporada vas a ver acá el porcentaje de cada jugador."
              />
            )}
                {/* SKELETON — siempre visible mientras carga */}
                {loading && (
                  <ul>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <SkeletonRow key={i} />
                    ))}
                  </ul>
                )}
                {!loading && !error && (
                  <ul
                    className={`transition-all duration-300 ease-premium
                      ${fadeIn ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}
                    `}
                  >
                  {table.getRowModel().rows.map((row, indice, filas) => {
                    const item = row.original;
                    const isExpanded = expandedPlayer === item.id;

                    // Los cortes se calculan contra la fila visible anterior,
                    // así siguen siendo correctos cuando un tramo queda partido
                    // entre dos páginas.
                    const zona = zonaDe(item);
                    const anterior =
                      indice > 0 ? filas[indice - 1].original : null;
                    const abreTramo = zona !== (anterior ? zonaDe(anterior) : null);
                    const enDescenso = estaEnDescenso(item);
                    const abreDescenso =
                      enDescenso && !(anterior && estaEnDescenso(anterior));

                // expansions
                const createdTime = new Date(item.createdAt).setHours(0, 0, 0, 0);
                const partialEvents = eventsThisYear.filter(
                  (evt) => (evt._dayOnly || 0) >= createdTime
                );
                const attendedEvents = partialEvents.filter((evt) =>
                  evt.Asistencia?.some(
                    (a) => a.PersonaId === item.id && a.presente === true
                  )
                );

                return (
               <React.Fragment key={item.id}>
               {/* El rótulo del descenso gana: si los dos cortes caen en la
                   misma fila, no se dibujan dos separadores seguidos. */}
               {abreDescenso ? (
                 <SeparadorDescenso />
               ) : abreTramo && indice > 0 ? (
                 <EspacioEntreTramos />
               ) : null}
               <motion.li
  layout
  onClick={() => toggleExpand(item.id)}
  aria-expanded={isExpanded}
  className={`group mb-2.5 flex cursor-pointer flex-col rounded-card border border-l-[3px] p-4 pl-[13px] transition-colors duration-150 dark:bg-navy-900 ${bordeDeZona[zona]} ${
    isExpanded
      ? "border-gray-300 bg-gray-50/60 dark:border-white/15"
      : "border-gray-200/80 bg-white hover:border-gray-300 hover:bg-gray-50/50 dark:border-white/5 dark:hover:border-white/15"
  }`}
  whileTap={{ scale: 0.995 }}
  transition={{ layout: { duration: 0.25 } }}
>
  {/* Row main line */}
  <div className="flex items-center justify-between gap-3">
    <span className="min-w-0 select-none truncate text-[15px] font-semibold text-gray-900 dark:text-white">
      {item.nombre}
    </span>

    <div className="flex shrink-0 items-center gap-2.5 sm:gap-3">
      {/* Percentage or 'Sin eventos' */}
      {item.partialTotal === 0 ? (
        <span className="text-[13px] font-medium text-gray-700 dark:text-gray-400">
          Sin eventos
        </span>
      ) : (
        <span className="tabular text-[15px] font-semibold text-gray-900 dark:text-white">
          {Math.round(Number(item.porcentaje) || 0)}%
        </span>
      )}

      {/* counts */}
      <span className="tabular text-[13px] text-gray-700 dark:text-gray-500">
        {item.asistencias}/{item.partialTotal}
      </span>

      <motion.span
        animate={{ rotate: isExpanded ? 180 : 0 }}
        transition={{ duration: 0.25 }}
        className="flex shrink-0 text-gray-600 transition-colors group-hover:text-gray-700 dark:text-gray-500"
      >
        <MdKeyboardArrowDown className="h-[18px] w-[18px]" />
      </motion.span>
    </div>
  </div>

  {/* Progress bar */}
  <div className="mt-3 flex items-center gap-3">
    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-navy-800">
      {/* Tono neutro a propósito: la zona ya la dice la franja del costado, y
          repetirla acá sería codificar dos veces la misma variable. La barra
          sólo comunica magnitud. */}
      <div
        className="h-full rounded-full bg-gray-800 transition-[width] duration-700 ease-premium dark:bg-gray-400"
        style={{
          width: item.partialTotal === 0 ? "0%" : `${Math.min(100, Math.round(Number(item.porcentaje) || 0))}%`,
        }}
      />
    </div>
  </div>

  {/* Expanded content (keeps your AnimatePresence behavior) */}
  <AnimatePresence initial={false}>
    {isExpanded && (
      <motion.div
        key={`exp-${item.id}`}
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{
          animate: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
          exit: { duration: 0.18, ease: "easeInOut" },
        }}
        className="mt-4 overflow-hidden"
      >
        <div className="border-t border-gray-200/80 pt-4 dark:border-white/10">
        <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-700 dark:text-gray-500">
          Eventos asistidos
        </h4>
        {attendedEvents.length === 0 ? (
          <p className="text-sm text-gray-700 dark:text-gray-400">
            Este jugador no participó de ningún evento.
          </p>
        ) : (
          <motion.ul
            variants={attendeeContainer}
            initial="hidden"
            animate="show"
            className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3"
          >
            {attendedEvents.map((e) => (
              <motion.li
                key={e.id}
                variants={attendeeItem}
                className="tabular flex items-center justify-center gap-2 rounded-control border border-gray-200/80 bg-white px-3 py-2 text-[13px] font-medium text-gray-900 dark:border-white/10 dark:bg-navy-800 dark:text-gray-200"
              >
                <span>{formatDate(e.fecha)}</span>
                {e.tipo === "Fútbol" ? (
                  <GiSoccerBall className="h-4 w-4 text-brand-500 dark:text-brand-200" />
                ) : e.tipo === "Peña" ? (
                  <GiBarbecue className="h-4 w-4 text-brand-500 dark:text-brand-200" />
                ) : null}
              </motion.li>
            ))}
          </motion.ul>
        )}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
</motion.li>
               </React.Fragment>

                );
              })}
            </ul>
            )}
          </div>

          {/* PAGINATION */}
          <Pagination
            className="mt-5 border-t border-gray-200/70 pt-5 dark:border-white/10"
            pageIndex={table.getState().pagination.pageIndex}
            pageCount={table.getPageCount()}
            canPreviousPage={table.getCanPreviousPage()}
            canNextPage={table.getCanNextPage()}
            onPreviousPage={() => table.previousPage()}
            onNextPage={() => table.nextPage()}
          />
        </Card>
      </div>
    </div>
  );
};

export default AsistenciaList;
